/**
 * Mac App Store In-App Purchase flow (Electron `inAppPurchase` / StoreKit).
 *
 * Only used in a Mac App Store build (isMacAppStore()). The native purchase
 * happens in the Electron main process; this module verifies the resulting
 * receipt with the same `verify-apple-purchase` edge function used by iOS,
 * which activates Pro for the signed-in user.
 */

import { supabase } from '@/integrations/supabase/client';
import { isMacAppStore } from '@/lib/desktopBridge';

/** Must match the IAP product ID configured in App Store Connect for the Mac app. */
export const MAC_IAP_PRO_PRODUCT_ID = 'pro_monthly';

type Result = { success: boolean; error?: string };

async function verifyReceipt(receiptData: string): Promise<Result> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.access_token) return { success: false, error: 'Not authenticated' };

  const { data, error } = await supabase.functions.invoke('verify-apple-purchase', {
    body: { receiptData, productId: MAC_IAP_PRO_PRODUCT_ID },
  });

  if (error) return { success: false, error: error?.message ?? 'Verification failed' };
  if (data?.error) return { success: false, error: typeof data.error === 'string' ? data.error : 'Verification failed' };
  return { success: true };
}

/** Run the full Mac App Store purchase flow: StoreKit purchase → backend verification. */
export async function purchaseProWithMacIAP(): Promise<Result> {
  if (!isMacAppStore()) {
    return { success: false, error: 'In-App Purchase is only available in the Mac App Store app.' };
  }

  const purchase = await window.electronAPI!.purchaseMacIap(MAC_IAP_PRO_PRODUCT_ID);
  if (!purchase.success || !purchase.receiptData) {
    return { success: false, error: purchase.error ?? 'Purchase did not complete.' };
  }

  return verifyReceipt(purchase.receiptData);
}

/** Restore a previous Mac App Store purchase and re-activate Pro. */
export async function restoreProWithMacIAP(): Promise<Result> {
  if (!isMacAppStore()) {
    return { success: false, error: 'Restore is only available in the Mac App Store app.' };
  }

  const restored = await window.electronAPI!.restoreMacIap();
  if (!restored.success || !restored.receiptData) {
    return { success: false, error: restored.error ?? 'No previous purchase found to restore.' };
  }

  return verifyReceipt(restored.receiptData);
}
